import { useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';
import { useShowInternals } from '../../contexts/InternalsContext';
import type { WatchTimeline as Timeline, WatchTimelinePoint } from '../../types';
import { ADMIN_TEXT } from '../common/AdminPill';
import AlertBox from './AlertBox';
import PageChanges from './PageChanges';
import SearchChanges from './SearchChanges';
import { changeSummary, deltaWords, foldQuiet } from './timelineFold';
import { when } from './watchEventLines';

/** The mark on the line for each kind of point. */
const DOT: Record<WatchTimelinePoint['kind'], string> = {
  changed: 'bg-indigo-500 border-indigo-500',
  baseline: 'bg-white border-gray-400 dark:bg-gray-900',
  quiet: 'bg-gray-300 border-gray-300 dark:bg-gray-600 dark:border-gray-600',
  failed: 'bg-white border-amber-500 border-dashed dark:bg-gray-900',
  skipped: 'bg-white border-gray-400 border-dashed dark:bg-gray-900',
  running: 'bg-emerald-400 border-emerald-400 animate-pulse',
};

function Point({
  point,
  title,
  probeKind,
  focused,
}: {
  point: WatchTimelinePoint;
  title?: string;
  probeKind?: string;
  /** The check you came from: scrolled to and lit up once. */
  focused?: boolean;
}) {
  const ref = useRef<HTMLLIElement>(null);
  useEffect(() => {
    if (focused) ref.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  const [open, setOpen] = useState(false);
  // A pages check opens onto its pages; the alert boxes are a click further.
  const [boxes, setBoxes] = useState(false);
  const internals = useShowInternals();
  const n = point.events.length;
  const lead = point.events[0];
  const pages = probeKind === 'sitemap';
  // A rank check opens onto its searches, a row each.
  const searches = probeKind === 'serp_rank';
  return (
    <li
      ref={ref}
      className={`relative -mx-2 rounded px-2 pb-5 pl-8 ${focused ? 'jc-pinpoint-block' : ''}`}
      data-testid={`timeline-point-${point.run_id}`}
    >
      <span className={`absolute left-[3px] top-1.5 h-2.5 w-2.5 rounded-full border-2 ${DOT[point.kind]}`} />
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{point.at ? when(point.at) : '-'}</span>
        {title && <span className="text-xs text-gray-500 dark:text-gray-400">{title}</span>}
        {point.reading && (
          <span className="ml-auto text-xs tabular-nums text-gray-600 dark:text-gray-300">
            {point.reading}
            {point.delta ? <span className="text-gray-400"> ({deltaWords(point.delta)})</span> : null}
          </span>
        )}
      </div>
      {point.kind === 'baseline' && (
        <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-300">First look: recorded as the starting point.</p>
      )}
      {point.kind === 'running' && (
        <p className="mt-0.5 text-xs text-emerald-700 dark:text-emerald-300">Checking now…</p>
      )}
      {(point.kind === 'failed' || point.kind === 'skipped') && (
        <p className="mt-0.5 text-xs text-amber-700 dark:text-amber-300">
          {point.kind === 'failed' ? "Couldn't check: " : 'Skipped: '}
          {point.reason}
          {internals && point.error && <span className={`block ${ADMIN_TEXT}`}>{point.error}</span>}
        </p>
      )}
      {point.partial.length > 0 && (
        <p
          className="mt-0.5 text-xs text-amber-700 dark:text-amber-300"
          data-testid={`timeline-partial-${point.run_id}`}
        >
          Read only part of the site: {point.partial.join('; ')}.
        </p>
      )}
      {point.suspect_against && (
        <p
          className="mt-0.5 text-xs text-amber-700 dark:text-amber-300"
          data-testid={`timeline-suspect-${point.run_id}`}
        >
          ⚠ Compared with a partial reading ({when(point.suspect_against)}): likely not a real change.
        </p>
      )}
      {point.kind === 'changed' && n === 0 && (
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">Changed, but too small to report.</p>
      )}
      {n > 0 && (
        <>
          <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-100">{lead.headline}</p>
          {n > 1 ? (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              className="mt-0.5 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
              data-testid={`timeline-changes-${point.run_id}`}
            >
              {open ? 'Hide' : `${n} changes ▸`}
              {!open && <span className="text-gray-500 dark:text-gray-400"> {changeSummary(point.events)}</span>}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setOpen(o => !o)}
              aria-expanded={open}
              className="mt-0.5 text-xs text-indigo-600 hover:underline dark:text-indigo-300"
            >
              {open ? 'Hide' : 'Details ▸'}
            </button>
          )}
          {open && pages && !boxes && (
            <>
              <PageChanges runId={point.run_id} events={point.events} />
              <button
                type="button"
                onClick={() => setBoxes(true)}
                className="mt-1 text-xs text-gray-500 hover:underline dark:text-gray-400"
              >
                Show the alerts
              </button>
            </>
          )}
          {open && searches && <SearchChanges runId={point.run_id} events={point.events} />}
          {open && !searches && (!pages || boxes) && (
            <ul className="mt-2 flex flex-col gap-1.5">
              {point.events.map(e => (
                <AlertBox key={e.id} event={e} tier={false} />
              ))}
            </ul>
          )}
        </>
      )}
    </li>
  );
}

/**
 * A watched subject's history as one line down the page: every check, not every
 * alert. A check that found something shows its reading, the change since the
 * last one and the alerts it raised; a quiet stretch is one line; a failed or
 * partial check says why. See `jcseo/watch/timeline.py`.
 */
export default function WatchTimeline({
  subjectId,
  refreshKey,
  focusRun,
}: {
  subjectId: number;
  refreshKey?: unknown;
  /** A check run to scroll to and light up, from a feed card. */
  focusRun?: number;
}) {
  const [data, setData] = useState<Timeline | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<number | 'all'>('all');
  useEffect(() => {
    let alive = true;
    api
      .getWatchTimeline(subjectId)
      .then(d => alive && setData(d))
      .catch(e => alive && setError(e instanceof Error ? e.message : 'Could not load the timeline'));
    return () => {
      alive = false;
    };
  }, [subjectId, refreshKey]);
  const rows = useMemo(
    () => foldQuiet((data?.points ?? []).filter(p => probe === 'all' || p.probe_id === probe)),
    [data, probe],
  );
  if (error) return <p className="text-xs text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="text-xs text-gray-400">Loading…</p>;
  if (!data.points.length) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        No checks yet. The first one records what is there now.
      </p>
    );
  }
  const titleOf = (id: number) => {
    const p = data.probes.find(x => x.id === id);
    return p ? p.label || p.title : '';
  };
  const many = data.probes.length > 1;
  return (
    <div data-testid="watch-timeline">
      {many && (
        <label className="mb-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          Show
          <select
            value={probe}
            onChange={e => setProbe(e.target.value === 'all' ? 'all' : Number(e.target.value))}
            className="rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            <option value="all">All checks</option>
            {data.probes.map(p => (
              <option key={p.id} value={p.id}>
                {p.label || p.title}
              </option>
            ))}
          </select>
        </label>
      )}
      <ol className="ml-1.5 border-l border-gray-200 dark:border-gray-700">
        {rows.map(row =>
          row.type === 'point' ? (
            <Point
              key={row.point.run_id}
              point={row.point}
              probeKind={data.probes.find(p => p.id === row.point.probe_id)?.kind}
              focused={row.point.run_id === focusRun}
              title={many && probe === 'all' ? titleOf(row.point.probe_id) : undefined}
            />
          ) : (
            <li key={row.key} className="relative pb-5 pl-6" data-testid="timeline-quiet">
              <span className="absolute left-[-1px] top-0 h-full border-l-2 border-dotted border-gray-300 dark:border-gray-600" />
              <div className="flex flex-wrap items-baseline gap-x-3 text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {row.from && row.to && row.from !== row.to
                    ? `${when(row.from)} – ${when(row.to)}`
                    : row.to
                      ? when(row.to)
                      : ''}
                  {' · '}
                  {row.count} check{row.count === 1 ? '' : 's'} · no change
                </span>
                {row.reading && <span className="ml-auto tabular-nums">{row.reading}</span>}
              </div>
            </li>
          ),
        )}
      </ol>
    </div>
  );
}
